import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Col, Form, Modal, Row, message } from 'antd';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { WebApiMemberDtoMemberDto, WebApiMemberDtoGroupDto, WebApiModelsMemberField, WebApiMemberDtoCustomMemberFieldValueDto, WebApiModelsMembershipProduct } from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { OneFrontModal } from '../Modals/OneFrontModal';
import { EmailNotificationPreference } from '../../Types/EmailNotificationPreference';
import { MemberStatusType } from '../../Types/MemberStatusType';
import { OutPutMemberEditFields } from '../Forms/OutPutMemberFields';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { validateMemberDuplicates } from '../../Helpers/MemberValidationHelper';

interface EditMemberProps {
    member: WebApiMemberDtoMemberDto;
    members: WebApiMemberDtoMemberDto[];
    membershipProducts: WebApiModelsMembershipProduct[];
    groups?: WebApiMemberDtoGroupDto[];
    memberFieldSettings: WebApiModelsMemberField[];
    user: { hasOnlyReadRights: boolean };
    loadMembers: () => void;
}

export const EditMember: React.FC<EditMemberProps> = (props) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const GetText = TextService.GetText;
    const [groups, setGroups] = useState([]);
    const [warningConfirmed, setWarningConfirmed] = useState(false);

    const onCancel = () => {
        form.setFieldValue("groupName", "");
        setOpen(false);
    };

    useEffect(() => {
        if (props.groups) {
            let groups: any = [];

            props.groups.forEach((group: WebApiMemberDtoGroupDto) => {
                if (!group.isDefaultGroup) {
                    groups.push({ value: group.id, label: group.name });
                }
            });
            setGroups(groups);
        }

    }, [props])

    useEffect(() => {
        form.setFieldValue("firstName", props.member.firstName);
        form.setFieldValue("lastName", props.member.lastName);
        form.setFieldValue("phoneNumber", props.member.phoneNumber);
        form.setFieldValue("email", props.member.email);
        form.setFieldValue("email2", props.member.email2);
        form.setFieldValue("address", props.member.address);
        form.setFieldValue("postalCode", props.member.postalCode);
        form.setFieldValue("postalArea", props.member.postalArea);
        form.setFieldValue("adressRow2", props.member.adressRow2);
        form.setFieldValue("adressRow3", props.member.adressRow3);
        form.setFieldValue("personalNumber", props.member.personalNumber);
        form.setFieldValue("webPage", props.member.webPage);
        form.setFieldValue("joinedDate", props.member.joinedDate ? dayjs(props.member.joinedDate) : null);
        form.setFieldValue("company", props.member.company);
        form.setFieldValue("country", props.member.country);
        form.setFieldValue("uniqueId", props.member.uniqueId);
        form.setFieldValue("groups", props.member.groups);
        form.setFieldValue("title", props.member.title);
        form.setFieldValue("extra1", props.member.extra1);
        form.setFieldValue("extra2", props.member.extra2);
        form.setFieldValue("extra3", props.member.extra3);
        form.setFieldValue("status", props.member.status == 1 ? true : false);
        form.setFieldValue("emailNotificationPreference", props.member.emailNotificationPreference == 1 ? true : false);
        form.setFieldValue("membership", props.member.membershipProduct ? props.member.membershipProduct : null);
        form.setFieldValue("membershipAddon", props.member.membershipProductAddOn ? props.member.membershipProductAddOn : []);

        // Populate custom fields from customFieldsJson
        if (props.member.customFieldValues) {
            try {
                if (Array.isArray(props.member.customFieldValues)) {
                    props.member.customFieldValues.forEach((field: WebApiMemberDtoCustomMemberFieldValueDto) => {
                        if (field && field.fieldId !== undefined && field.fieldValue !== undefined) {
                            // Find the field definition to check uiControl
                            const fieldDef = props.memberFieldSettings?.find((f: any) => f.id === field.fieldId);
                            if (fieldDef && fieldDef.uiControl === 1) { // 1 = DatePicker
                                // Accept YYYY-MM-DD, ISO 8601, or Date object
                                const isValidDate = (val: any) => {
                                    if (!val) return false;
                                    if (typeof val === 'string') {
                                        // Accept YYYY-MM-DD or ISO 8601 (e.g., 2025-08-13T22:00:00.000Z)
                                        return /^\d{4}-\d{2}-\d{2}$/.test(val) || !isNaN(Date.parse(val));
                                    }
                                    if (val instanceof Date && !isNaN(val.getTime())) return true;
                                    return false;
                                };
                                if (isValidDate(field.fieldValue)) {
                                    let dateVal = dayjs(field.fieldValue.toString());
                                    form.setFieldValue(field.fieldId.toString(), dateVal);
                                }
                            } else {
                                form.setFieldValue(field.fieldId.toString(), field.fieldValue);
                            }
                        }
                    });
                }
            } catch (e) {
                // ignore parse errors
            }
        }
    }, [form, props.member])

    const setFieldCustomError = (fieldName: string, error: string,) => {
        form.setFields([
            {
                name: fieldName, // required
                errors: [error],
            },
        ]);
    }

    const onCreate = async (values: any) => {
        // Validate member duplicates
        const validationResult = validateMemberDuplicates(
            values,
            props.members,
            props.memberFieldSettings,
            props.member.memberId
        );

        // Handle errors (blocking)
        if (validationResult.hasErrors) {
            validationResult.errors.forEach(error => {
                setFieldCustomError(error.fieldName, error.message);
            });
            return true;
        }

        // Handle warnings (non-blocking with confirmation)
        if (validationResult.hasWarnings && !warningConfirmed) {
            const warningMessages = validationResult.warnings.map(w => w.message).join('\n\n');
            Modal.confirm({
                title: 'Varning',
                icon: <ExclamationCircleOutlined />,
                content: warningMessages,
                okText: 'Fortsätt ändå',
                cancelText: 'Avbryt',
                onOk: () => {
                    setWarningConfirmed(true);
                    form.submit();
                }
            });
            return true;
        }

        if (false) {
            return true;
        }

        var memberProducts = [];
        if (values.membership) {
            memberProducts.push(values.membership);
        }

        if (values.membershipAddon && Array.isArray(values.membershipAddon)) {
            memberProducts.push(...values.membershipAddon);
        }


        const customFields: WebApiMemberDtoCustomMemberFieldValueDto[] = [];

        props.memberFieldSettings.forEach((field: WebApiModelsMemberField) => {
            if (field.columnType === MemberFieldTypes.Custom) {
                customFields.push({
                    fieldId: field.id!,
                    fieldValue: values[field.id!]
                });
            }
        });

        try {

            await MemberService.UpdateMember({
                memberId: props.member.memberId,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                email2: values.email2,
                phoneNumber: values.phoneNumber,
                address: values.address,
                postalArea: values.postalArea,
                adressRow2: values.adressRow2,
                adressRow3: values.adressRow3,
                personalNumber: values.personalNumber,
                webPage: values.webPage,
                joinedDate: values.joinedDate,
                postalCode: values.postalCode,
                country: values.country,
                company: values.company,
                uniqueId: values.uniqueId,
                title: values.title,
                extra1: values.extra1,
                extra2: values.extra2,
                extra3: values.extra3,
                customFieldValues: customFields,
                memberProducts: memberProducts,
                groups: values.groups,
                status: values.status == true ? MemberStatusType.Cancelled : MemberStatusType.Active,
                emailNotificationPreference: values.emailNotificationPreference == true ? EmailNotificationPreference.NoInformationEmails : EmailNotificationPreference.Unspecified
            });
        } catch (error) {
            message.warning(GetText("EditMember_ErrorSaving"));
            return;
        }
        setOpen(false);

        props.loadMembers();
    }

    const onPhoneChange = (value: any) => {
        form.setFieldValue("phoneNumber", value);
    }

    return (
        <>
            <OneFrontModal
                open={open}
                title={GetText("EditMember_ModalTitle")}
                okText={GetText("EditMember_ModalOk")}
                cancelText={GetText("EditMember_ModalCancel")}
                width={700}
                onCancel={onCancel}
                okButtonProps={{ disabled: props.user.hasOnlyReadRights }}
                onOk={() => {
                    form
                        .validateFields()
                        .then((values) => {
                            onCreate(values);
                        })
                        .catch((info) => {
                            console.log('Validate Failed:', info);
                        });
                }}
            >
                {props.member.unconfirmedEmail && props.member.unconfirmedEmail.length > 0 ? <i>Medlemmen har inte slutfört registreringen ännu. Registrerade sig med {props.member.unconfirmedEmail}</i> : ""}
                <Form disabled={props.user.hasOnlyReadRights} colon={false} form={form} labelAlign={"left"} layout='vertical' onFinish={onCreate}>
                    <Col span={24}>
                        <OutPutMemberEditFields
                            memberFieldSettings={props.memberFieldSettings}
                            membershipProducts={props.membershipProducts}
                            GetText={GetText}
                            groups={groups}
                            user={props.user}
                            onPhoneChange={onPhoneChange}
                            isAdmin={true}
                        />
                    </Col>
                </Form>
            </OneFrontModal>

            <EditOutlined
                style={{ fontSize: "20px" }}
                type="primary"
                onClick={() => {
                    setOpen(true);
                }}
            >
            </EditOutlined>
        </>
    );
};

export default EditMember;